import React from 'react';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  GraduationCap,
  ClipboardCheck,
  Award,
  FileCheck2,
  TrendingUp,
  FileText,
  UserCheck,
  LogOut,
  Building2,
  BarChart3,
  Clock,
  AlertTriangle,
  Download,
  LineChart,
  BookOpen,
  Settings as SettingsIcon,
  ShieldAlert,
  FileSpreadsheet,
  Users,
  Sliders
} from 'lucide-react';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, isOpen, onClose }) => {
  const { user, logout } = useAuth();

  const isDEO = user?.role === 'deo' || user?.role === 'admin';
  const isBEO = user?.role === 'beo';
  const isSchool = user?.role === 'school' || user?.role === 'principal' || user?.role === 'teacher';

  // Strict Menu for SCHOOL Role
  const schoolNavItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'daily-attendance', label: 'Daily Attendance (6–12)', icon: UserCheck, badge: 'Daily' },
    { id: 'student-lifecycle', label: 'Student Lifecycle & Status', icon: Users },
    { id: 'student-data', label: 'Student Data', icon: GraduationCap },
    { id: 'monthly-assessment', label: 'Monthly Assessment', icon: ClipboardCheck },
    { id: 'sat-marks', label: 'SAT Marks', icon: Award },
    { id: 'submission-status', label: 'Submission Status', icon: Clock },
    { id: 'school-performance', label: 'Performance', icon: TrendingUp },
    { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
    { id: 'profile', label: 'Profile', icon: UserCheck }
  ];

  // Strict Menu for BEO Role
  const beoNavItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'daily-attendance', label: 'Daily Attendance Monitoring', icon: UserCheck, badge: '6-12' },
    { id: 'student-lifecycle', label: 'Student Lifecycle & Status', icon: Users },
    { id: 'schools', label: 'Schools', icon: Building2 },
    { id: 'assessment-monitoring', label: 'Assessment Monitoring', icon: ClipboardCheck },
    { id: 'sat-monitoring', label: 'SAT Monitoring', icon: Award },
    { id: 'school-performance', label: 'School Performance', icon: TrendingUp },
    { id: 'historical-data', label: 'Historical Data', icon: LineChart },
    { id: 'block-analysis', label: 'Block Analysis', icon: BarChart3 },
    { id: 'pending-submissions', label: 'Pending Submissions', icon: Clock },
    { id: 'low-performing-schools', label: 'Low Performing Schools', icon: AlertTriangle },
    { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
    { id: 'export', label: 'Export', icon: Download },
    { id: 'profile', label: 'Profile', icon: UserCheck }
  ];

  // Strict Menu for DEO Role
  const deoNavItems: NavItem[] = [
    { id: 'dashboard', label: 'DEO Dashboard', icon: LayoutDashboard },
    { id: 'daily-attendance', label: 'Daily Attendance (6–12)', icon: UserCheck, badge: 'Daily' },
    { id: 'school-management', label: 'School Directory & Master', icon: Building2 },
    { id: 'student-analysis', label: 'Student Directory & Master', icon: GraduationCap },
    { id: 'student-lifecycle', label: 'Student Lifecycle & Status', icon: Users },
    { id: 'district-overview', label: 'District Overview', icon: Building2 },
    { id: 'block-performance', label: 'Block Performance', icon: BarChart3 },
    { id: 'school-performance', label: 'School Performance', icon: TrendingUp },
    { id: 'historical-data', label: 'Historical Data', icon: LineChart },
    { id: 'data-import', label: 'Import Data', icon: FileSpreadsheet },
    { id: 'user-management', label: 'User Management', icon: UserCheck },
    { id: 'assessment-monitoring', label: 'Assessment Monitoring', icon: ClipboardCheck },
    { id: 'assessment-config', label: 'Assessment Max Marks Config', icon: Sliders },
    { id: 'sat-monitoring', label: 'SAT Monitoring', icon: Award },
    { id: 'subject-analysis', label: 'Subject Analysis', icon: BookOpen },
    { id: 'low-performing-schools', label: 'Low Performing Schools', icon: AlertTriangle },
    { id: 'pending-submissions', label: 'Pending Submissions', icon: Clock },
    { id: 'monthly-trends', label: 'Monthly Trends', icon: LineChart },
    { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
    { id: 'executive-report', label: 'Executive Report', icon: FileText },
    { id: 'export', label: 'Export', icon: Download },
    { id: 'settings', label: 'Settings', icon: SettingsIcon }
  ];

  let currentNavItems: NavItem[] = schoolNavItems;
  if (isDEO) {
    currentNavItems = deoNavItems;
  } else if (isBEO) {
    currentNavItems = beoNavItems;
  }

  const getRoleHeader = () => {
    if (isDEO) return { title: 'DEO PORTAL', sub: 'District Administration' };
    if (isBEO) return { title: 'BEO PORTAL', sub: `Block: ${user?.block || 'Nuh'}` };
    return { title: 'SCHOOL PORTAL', sub: 'School Administration' };
  };

  const roleInfo = getRoleHeader();

  const handleSelect = (id: string) => {
    setActiveTab(id);
    onClose();
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-40 lg:hidden"
        />
      )}

      {/* Official Government Sidebar */}
      <aside
        className={`fixed top-[61px] bottom-0 left-0 z-40 w-60 bg-[#0B1E38] text-slate-200 border-r border-slate-800 transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Role Scope Header */}
        <div className="px-3.5 py-2.5 border-b border-slate-800/80 bg-[#071527]">
          <div className="text-[10px] font-bold tracking-wider text-amber-400 uppercase">
            {roleInfo.title}
          </div>
          <div className="text-xs text-slate-200 font-semibold truncate mt-0.5">
            {roleInfo.sub}
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {currentNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleSelect(item.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded transition-colors ${
                  isActive
                    ? 'bg-[#1E40AF] text-white font-semibold shadow-sm'
                    : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
                }`}
              >
                <div className="flex items-center space-x-2.5 min-w-0">
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-amber-400' : 'text-slate-400'}`} />
                  <span className="truncate">{item.label}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Logout at bottom of Sidebar */}
        <div className="p-2 border-t border-slate-800 bg-[#071527]">
          <button
            onClick={logout}
            className="w-full flex items-center justify-start space-x-2.5 px-3 py-2 text-xs font-medium text-rose-300 hover:bg-rose-950/40 hover:text-rose-200 rounded transition"
          >
            <LogOut className="w-4 h-4 text-rose-400" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};
